/**
 * Newsletter banner form gating and inline validation.
 *
 * The merchant asked for three things: submit unavailable until an email is
 * entered and the consent box is ticked, a red border on a bad email, and the
 * error spelled out underneath the field.
 *
 * Production already does this in `window.EmailMarketingFormValidator`
 * (ocs.ca js_theme.js). The timing rules below are lifted from it, because they
 * are the difference between helpful and nagging:
 *
 *   - an error appears only once a field has been touched, and for the email
 *     only when it is also non-empty, so an untouched form is quiet;
 *   - once a field is showing an error it re-validates on every keystroke, so
 *     the error clears the moment the value becomes good;
 *   - a field being typed into does not sprout a new error mid-word.
 *
 * Two deliberate departures from production:
 *
 *   1. `aria-disabled`, not the `disabled` property. A disabled button leaves
 *      the tab order entirely — a keyboard or screen-reader user never meets it
 *      and is never told why the form will not go. Here it stays focusable, and
 *      activating it reveals what is missing and sends focus there.
 *   2. Everything is applied by this script rather than rendered by Liquid.
 *      Production ships `disabled="disabled"` in the HTML, so if its script
 *      fails the form cannot be submitted by anyone. Gating from here means no
 *      JS gives you a plain form with the browser's own validation, which is
 *      worse than this but far better than a dead button.
 *
 * Validity comes from checkValidity(), so the rules stay declared where they
 * are visible — the type="email", required and pattern already on the input in
 * sections/newsletter-banner.liquid. A regex here would be a second source of
 * truth, free to drift from the markup.
 *
 * computeState is exported for tests: it is the whole policy, and it is pure.
 */
(function () {
  'use strict';

  var SELECTOR_FORM = '.newsletter-banner__form';
  var SELECTOR_EMAIL = '.newsletter-banner__input';
  var SELECTOR_CONSENT = '.newsletter-banner__checkbox';
  var SELECTOR_SUBMIT = '.newsletter-banner__submit';
  var SELECTOR_EMAIL_ERROR = '.newsletter-banner__message--email';
  var SELECTOR_CONSENT_ERROR = '.newsletter-banner__message--consent';
  var SELECTOR_SUCCESS = '.newsletter-banner__success';

  var EVENT_NAME = 'ocs:marketing_signup';
  var EVENT_SECTION = 'MARKETING SIGNUP';
  var EVENT_RESULT = 'successful';
  /**
   * Forms already wired, held by identity rather than marked in the DOM.
   *
   * An attribute would be wrong here: it serialises. A re-rendered section
   * carries the marker into its fresh HTML, so the new form — which has no
   * listeners at all — looks already bound and gets skipped, leaving a button
   * that does nothing. Identity cannot be copied by innerHTML, and a form that
   * goes away is collected with the node.
   */
  var bound = new WeakSet();

  /**
   * How many times each success message has been jumped to.
   *
   * Two passes, deliberately. The first runs as soon as the markup exists, so
   * that a slow connection does not leave the user sitting at the top of the
   * page for however long the images above take — waiting for load alone meant
   * exactly that. The second runs once everything has loaded and corrects the
   * position, because the page grows by thousands of pixels in between.
   *
   * Capped so that a section re-render cannot keep yanking the user back.
   */
  var passes = new WeakMap();
  var MAX_PASSES = 2;

  /** Success messages already reported, so the two passes publish once. */
  var published = new WeakSet();

  /**
   * The entire gating policy, as a pure function of what the user has done.
   *
   * @param {object} input
   * @param {string} input.emailValue        current value, untrimmed
   * @param {boolean} input.emailValid       what checkValidity() says
   * @param {boolean} input.consentChecked
   * @param {boolean} input.emailTouched     has been focused at least once
   * @param {boolean} input.consentTouched
   * @param {boolean} input.submitAttempted  submit or a disabled-button press
   * @returns {{canSubmit: boolean, showEmailError: boolean, showConsentError: boolean}}
   */
  function computeState(input) {
    var state = input || {};
    var emailValid = state.emailValid === true;
    var consentChecked = state.consentChecked === true;
    var submitAttempted = state.submitAttempted === true;

    var emailFilled = String(state.emailValue == null ? '' : state.emailValue).trim() !== '';

    // An empty field is flagged only once the user has actually tried to send
    // the form. Merely tabbing through an empty box is not a mistake yet, so
    // blur stays quiet — but pressing submit has to account for every reason
    // the form will not go, and "you left the email blank" is one of them.
    //
    // Production shows nothing at all for an empty email. It can afford to:
    // its button is natively disabled, so there is no press to answer. Ours is
    // deliberately pressable, which is exactly what makes the silence a gap.
    var emailAnswerable = emailFilled ? state.emailTouched === true || submitAttempted : submitAttempted;

    return {
      canSubmit: emailValid && consentChecked,
      showEmailError: !emailValid && emailAnswerable,
      showConsentError: !consentChecked && (state.consentTouched === true || submitAttempted),
    };
  }

  function setHidden(element, hidden) {
    if (!element) return;
    if (hidden) element.setAttribute('hidden', '');
    else element.removeAttribute('hidden');
  }

  /**
   * @param {Element} form
   */
  function init(form) {
    if (!form || typeof form.querySelector !== 'function') return;

    // Idempotent, because initAll runs again on every section re-render and a
    // form that survived one would otherwise collect a second set of
    // listeners — and a second revealAll() moving focus on the same click.
    if (bound.has(form)) return;
    bound.add(form);

    var email = form.querySelector(SELECTOR_EMAIL);
    var consent = form.querySelector(SELECTOR_CONSENT);
    var submit = form.querySelector(SELECTOR_SUBMIT);

    if (!email || !consent || !submit) return;

    var emailError = form.querySelector(SELECTOR_EMAIL_ERROR);
    var consentError = form.querySelector(SELECTOR_CONSENT_ERROR);

    var emailTouched = false;
    var consentTouched = false;
    var submitAttempted = false;

    // Ours to validate from here on. Set in JS, not in the markup, so that a
    // failed script leaves the browser's own validation in place.
    form.noValidate = true;

    function read() {
      return computeState({
        emailValue: email.value,
        emailValid: typeof email.checkValidity === 'function' ? email.checkValidity() : true,
        consentChecked: consent.checked === true,
        emailTouched: emailTouched,
        consentTouched: consentTouched,
        submitAttempted: submitAttempted,
      });
    }

    function render() {
      var state = read();

      submit.setAttribute('aria-disabled', state.canSubmit ? 'false' : 'true');
      email.setAttribute('aria-invalid', state.showEmailError ? 'true' : 'false');
      setHidden(emailError, !state.showEmailError);
      setHidden(consentError, !state.showConsentError);

      return state;
    }

    /** Whether an error is already on screen for this field. */
    function emailShowingError() {
      return !!emailError && !emailError.hasAttribute('hidden');
    }

    // Touched is set on the way out, not the way in. Leaving a field is proof
    // enough of having entered it, and it is one fewer thing to be true: a
    // focus event that never arrives (programmatic focus, a field restored by
    // the browser) would otherwise keep the error suppressed forever.
    email.addEventListener('blur', function () {
      emailTouched = true;
      render();
    });

    email.addEventListener('input', function () {
      // Only re-render while an error is up, so that nothing new appears
      // part-way through typing an address that is not finished yet.
      if (emailShowingError() || submitAttempted) render();
      else submit.setAttribute('aria-disabled', read().canSubmit ? 'false' : 'true');
    });

    consent.addEventListener('change', function () {
      consentTouched = true;
      render();
    });

    /** Reveal everything outstanding and put the user on the first problem. */
    function revealAll() {
      submitAttempted = true;
      emailTouched = true;
      consentTouched = true;

      var state = render();
      var target = state.showEmailError || !email.checkValidity() ? email : consent;

      if (typeof target.focus === 'function') target.focus();

      return state;
    }

    submit.addEventListener('click', function (event) {
      if (submit.getAttribute('aria-disabled') !== 'true') return;

      // aria-disabled is advisory — the browser would still submit — so the
      // press has to be stopped here rather than by the form handler alone.
      event.preventDefault();
      revealAll();
    });

    form.addEventListener('submit', function (event) {
      // Reachable without touching the button at all: Enter in a text field
      // submits the form implicitly.
      if (read().canSubmit) return;

      event.preventDefault();
      revealAll();
    });

    render();
  }

  /**
   * Run once the age gate is out of the way, or immediately if it never was.
   *
   * While the gate is up the page cannot scroll (html.agewall-active body sets
   * overflow: hidden, assets/ocs-age-gate.css:17) and everything behind it is
   * inert, so moving focus there would put it somewhere the user cannot reach.
   * The gate announces nothing when it closes, so the class on <html> is the
   * signal available.
   */
  function whenPageIsReachable(callback) {
    var html = document.documentElement;
    var gateIsUp = html && html.classList && html.classList.contains('agewall-active');

    if (!gateIsUp) {
      callback();
      return;
    }

    if (typeof MutationObserver !== 'function') return;

    var observer = new MutationObserver(function () {
      if (html.classList.contains('agewall-active')) return;

      observer.disconnect();

      // verify() removes the class and then focuses #MainContent, all in one
      // synchronous run. Going now would hand focus straight back; a task
      // boundary puts us after it.
      setTimeout(callback, 0);
    });

    observer.observe(html, { attributes: true, attributeFilter: ['class'] });
  }

  /**
   * Put the user on the confirmation after a successful signup.
   *
   * Shopify posts the form and returns to ?customer_posted=true#<form id>, and
   * the browser's own jump to that fragment is unreliable here: the anchor is
   * ~6000px down a page whose images are still loading, so the target keeps
   * moving for about two seconds, and Chrome abandons the jump entirely the
   * moment the user scrolls. Landing halfway up the page after signing up
   * reads as though nothing happened.
   *
   * Waiting for load means the images above have finished and the position is
   * final. Focus moves too, not just scroll — the message carries role="status"
   * and tabindex="-1" precisely so it can receive it, and a sighted-only scroll
   * leaves a screen-reader user where they were.
   */
  /**
   * Report the signup, through the bridge that owns Shopify publication.
   *
   * assets/ocs-analytics-events.js is the sole owner of the Shopify analytics
   * publication call — see the ARCH-001 guard in tests/analytics, which reads
   * source text and so is tripped even by naming that call in a comment. Every
   * event here goes through the bridge, and never near dataLayer.
   *
   * Production pushes {event:'CTA', section:'MARKETING SIGNUP',
   * marketing_signup:'successful'} from its submit handler, so the legacy names
   * are carried over exactly; the Custom Pixel maps this back to `CTA`.
   *
   * Where it fires differs, and deliberately. Production fires on submit, the
   * instant client validation passes, which counts signups the server may go on
   * to reject and — through the pixel sandbox, which is asynchronous across a
   * worker boundary — would very likely be lost to the navigation that follows
   * it anyway. This fires on the confirmation Shopify renders back, so the
   * event means the signup was actually accepted.
   *
   * @param {Element} success
   */
  function reportSignup(success) {
    if (published.has(success)) return;
    published.add(success);

    try {
      var api = window.OCSAnalyticsEvents;

      if (!api || typeof api.publishOcsAnalyticsEvent !== 'function') return;

      var searchAnalytics = window.OCSSearchAnalytics;
      var locale = searchAnalytics && typeof searchAnalytics.readLocale === 'function'
        ? searchAnalytics.readLocale(document)
        : '';
      var ageVerified = searchAnalytics && typeof searchAnalytics.readAgeVerified === 'function'
        ? searchAnalytics.readAgeVerified(document) === true
        : false;

      api.publishOcsAnalyticsEvent(EVENT_NAME, {
        section: EVENT_SECTION,
        marketing_signup: EVENT_RESULT,
        locale: locale,
        age_verified: ageVerified,
      });
    } catch (error) {
      /* Analytics must never break the confirmation the user came here for. */
    }
  }

  function revealSuccess(root) {
    var scope = root && typeof root.querySelector === 'function' ? root : document;
    var success = scope.querySelector(SELECTOR_SUCCESS);

    if (!success) return;

    // Ahead of the age-gate wait below: the signup happened either way, and
    // holding the event back behind a modal would only risk losing it.
    reportSignup(success);

    var seen = passes.get(success) || 0;

    if (seen >= MAX_PASSES) return;
    passes.set(success, seen + 1);

    whenPageIsReachable(function () {
      if (typeof success.scrollIntoView === 'function') {
        // Deliberately not smooth: base.css sets scroll-behavior globally, and
        // animating six thousand pixels on arrival is a long ride to a message
        // the user is waiting on.
        success.scrollIntoView({ block: 'center', behavior: 'instant' });
      }

      // preventScroll, because scrollIntoView has already chosen the position
      // and focus() would otherwise redo it with its own idea of "visible".
      if (typeof success.focus === 'function') success.focus({ preventScroll: true });
    });
  }

  function initAll(root) {
    var scope = root && typeof root.querySelectorAll === 'function' ? root : document;
    var forms = scope.querySelectorAll(SELECTOR_FORM);

    for (var i = 0; i < forms.length; i += 1) init(forms[i]);
  }

  window.OCSNewsletterBannerForm = {
    computeState: computeState,
    init: init,
    initAll: initAll,
    revealSuccess: revealSuccess,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function bootstrap() {
      initAll(document);
      revealSuccess(document);
    });
  } else {
    initAll(document);
    revealSuccess(document);
  }

  // The corrective second pass. The page keeps growing under the message while
  // the images above it load, so the position chosen a moment ago is already
  // stale; by load it is final.
  if (document.readyState !== 'complete') {
    window.addEventListener('load', function settled() {
      revealSuccess(document);
    });
  }

  // A re-rendered section arrives as fresh markup with no listeners on it: the
  // <script src> above is not re-executed when Shopify swaps a section's HTML
  // in, so without this the button in the theme editor — and after any morph —
  // is inert, and pressing it does nothing at all. Same hook as
  // assets/ocs-header-mega-panels.js:717.
  document.addEventListener('shopify:section:load', function (event) {
    var scope = (event && event.target) || document;

    initAll(scope);
    revealSuccess(scope);
  });
})();
